'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  Loader2,
  Plus,
  Printer,
  ScanLine
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PermissionGate } from '@/features/auth';
import {
  DeviceFormDialog,
  dedupeScannerDevices,
  deviceQueries,
  normalizeScannerName,
  type Device,
  type DiscoveredDevice
} from '@/features/devices';
import { useScanFromCameraMutation } from '../services/photo-scanning.mutations';
import { decodeQrFromBlob } from '@/lib/qr/decode';
import { randomId } from '@/lib/random';
import { buildInjectedPayload, injectQrIntoBlob } from '../utils/inject-qr';
import { useDevQrInjectStore } from '../store/dev-qr-inject-store';
import { useLabelPrintConfig } from '@/features/configurations';
import { useAuth } from '@/contexts/auth-context';
import { isDevToolsAllowedForEmail } from '@/features/dev-tools';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { PHOTO_SCANNING_HASH } from '../constants';
import {
  loadDwtSdk,
  classifyDwtError,
  type DynamsoftStatic,
  type DwtInstance,
  type DwtDevice
} from '@/lib/scanner/load-dwt';

const INSTANCE_PREFIX = 'safha-document-scanner';
const STORAGE_KEY = 'dwt-scanner-settings';

// Hard ceiling on the SDK/local-service handshake. `CreateDWTObjectEx` and
// `GetDevicesAsync` can hang indefinitely when the Dynamsoft Service is
// unreachable — they neither resolve nor reject — which left the panel stuck on
// "connecting…" forever (operators reported multi-minute spinners). Past this we
// give up and fall into the `error` phase, which surfaces the upload fallback
// instead of an endless spinner.
const BOOT_TIMEOUT_MS = 20_000;
// Marker stored in `errorMessage` when the boot timed out, so the error branch
// can show a timeout-specific title instead of running the raw text through
// `classifyDwtError`.
const BOOT_TIMEOUT_SENTINEL = '__dwt_boot_timeout__';

function makeInstanceId(): string {
  return `${INSTANCE_PREFIX}-${randomId()}`;
}

// TWAIN PixelType constants (Dynamsoft documents these as fixed values)
const PIXEL_TYPE = { bw: 0, gray: 1, color: 2 } as const;

type ColorMode = keyof typeof PIXEL_TYPE;
type Resolution = 200 | 300 | 600;

interface ScannerSettings {
  deviceIdx: string;
  duplex: boolean;
  colorMode: ColorMode;
  resolution: Resolution;
  skipBlanks: boolean;
}

const DEFAULT_SETTINGS: ScannerSettings = {
  deviceIdx: '',
  duplex: false,
  colorMode: 'color',
  resolution: 200,
  skipBlanks: true
};

function readPersistedSettings(): ScannerSettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<ScannerSettings>;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function formatError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object') {
    const obj = err as { code?: number; message?: string };
    if (typeof obj.message === 'string') {
      return obj.code != null ? `${obj.code}: ${obj.message}` : obj.message;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err);
}

// ── Component ────────────────────────────────────────────────────────────────

interface DwtScannerPanelProps {
  batchId: number;
  nextSequence: number;
  total: number;
  /**
   * Sourced from the registration's `dynamic_form.is_double_sided`. The
   * scanner SDK's `IfDuplexEnabled` is wired to this value directly so
   * operators can no longer toggle duplex per-batch — the dynamic form
   * is the single source of truth for sided-ness.
   */
  isDoubleSided: boolean;
  /**
   * Used only by the dev-only QR injector to synthesize a `match` payload
   * (`${containerId}-P${seq}`). Optional — the injector falls back to a
   * placeholder when not supplied.
   */
  containerId?: string | null;
  /**
   * QR-quarantine intake. When provided, every acquired page (after any
   * dev-QR injection) is handed off to the parent's `intakeImages` funnel
   * INSTEAD of being uploaded directly — so clean `success` pages are added
   * instantly and everything else is held back for review. The parent owns
   * QR decode + classification, so the panel just forwards raw blobs.
   *
   * `replaceItemId`, when set, is forwarded so the parent replaces a specific
   * held quarantine item's bytes (a per-item "re-scan with scanner"). It is
   * cleared via `onAcquireDone` once the job finishes.
   */
  onAcquireBlob?: (
    blob: Blob,
    opts?: { replaceItemId?: string }
  ) => void | Promise<void>;
  /** Held quarantine item the next scan job should replace (one-shot). */
  rescanTargetId?: string | null;
  /** Fired when a scan job finishes so the parent can clear `rescanTargetId`. */
  onAcquireDone?: () => void;
}

export function DwtScannerPanel({
  batchId,
  nextSequence,
  total,
  isDoubleSided,
  containerId,
  onAcquireBlob,
  rescanTargetId,
  onAcquireDone
}: DwtScannerPanelProps) {
  const t = useTranslations('photoScanning');
  const { user } = useAuth();
  const isDevTools = isDevToolsAllowedForEmail(user?.email);
  const scanMutation = useScanFromCameraMutation();
  // Dev QR injector sizes its synthetic QR to the configured physical label
  // footprint (square → smaller edge) so it matches a real printed sticker.
  const { labelWidthMm, labelHeightMm } = useLabelPrintConfig();
  const labelMm = Math.min(labelWidthMm, labelHeightMm);
  // Keep the intake-funnel handoffs in refs so the long-lived SDK callback
  // always sees the current closures (mirrors how `handleNewPage` is held in
  // `onNewPageRef`).
  const onAcquireBlobRef = useRef(onAcquireBlob);
  const rescanTargetRef = useRef(rescanTargetId);
  useEffect(() => {
    onAcquireBlobRef.current = onAcquireBlob;
  }, [onAcquireBlob]);
  useEffect(() => {
    rescanTargetRef.current = rescanTargetId;
  }, [rescanTargetId]);

  const dwtRef = useRef<DwtInstance | null>(null);
  const dynamsoftRef = useRef<DynamsoftStatic | null>(null);
  const sequenceRef = useRef(nextSequence);
  // Last sequence accepted in the current scan job; pages beyond this are
  // dropped client-side even if the scanner produces extra ones.
  const maxSequenceRef = useRef<number>(0);
  const onNewPageRef = useRef<() => void>(() => {});

  const [phase, setPhase] = useState<
    'loading' | 'ready' | 'scanning' | 'error'
  >('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [devices, setDevices] = useState<DwtDevice[]>([]);
  const [settings, setSettings] = useState<ScannerSettings>(DEFAULT_SETTINGS);
  const { open: deviceFormOpen, onOpenChange: setDeviceFormOpen } =
    useHashDialog(PHOTO_SCANNING_HASH.deviceForm);
  // Track whether localStorage has been read into state. Skipping persistence
  // until then prevents the initial DEFAULT_SETTINGS render from overwriting
  // the user's saved values (StrictMode runs effects twice — without this
  // guard the second run reads the corrupted defaults back).
  const [hydrated, setHydrated] = useState(false);
  const [counters, setCounters] = useState({
    scanned: 0,
    uploaded: 0,
    failed: 0
  });

  // Hydrate settings from localStorage (client-only, after mount to avoid SSR mismatch)
  useEffect(() => {
    setSettings(readPersistedSettings());
    setHydrated(true);
  }, []);

  // Persist settings on every change — but only after the initial hydration.
  useEffect(() => {
    if (!hydrated || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // ignore quota / private-mode failures
    }
  }, [settings, hydrated]);

  function patch<K extends keyof ScannerSettings>(
    key: K,
    value: ScannerSettings[K]
  ) {
    setSettings((s) => ({ ...s, [key]: value }));
  }

  // Look up registered scanner-type Devices so we can show whether the
  // physically-connected scanner is already managed in /dashboard/devices.
  const selectedDwtDevice =
    settings.deviceIdx !== '' ? devices[Number(settings.deviceIdx)] : undefined;

  const scannerDevicesQuery = useQuery({
    ...deviceQueries.list({ device_type: ['scanner'], per_page: 100 }),
    enabled: phase === 'ready' && devices.length > 0
  });
  const scannerDevicesPage = scannerDevicesQuery.data;
  const isCheckingDeviceRegistry =
    scannerDevicesQuery.isLoading || scannerDevicesQuery.isFetching;

  const linkedDevice: Device | null = useMemo(() => {
    if (!selectedDwtDevice || !scannerDevicesPage?.data) return null;
    const target = normalizeScannerName(
      selectedDwtDevice.displayName
    ).toLowerCase();
    // Prefer the immutable scanner_identifier so renaming a Device's name
    // doesn't break the link to the physical scanner. Fall back to a
    // name-based match for legacy rows registered before scanner_identifier
    // was added.
    const byIdentifier = scannerDevicesPage.data.find(
      (d) => (d.scanner_identifier ?? '').toLowerCase() === target
    );
    if (byIdentifier) return byIdentifier;
    return (
      scannerDevicesPage.data.find(
        (d) =>
          !d.scanner_identifier &&
          normalizeScannerName(d.name).toLowerCase() === target
      ) ?? null
    );
  }, [selectedDwtDevice, scannerDevicesPage]);

  const devicePrefill: DiscoveredDevice | null = useMemo(() => {
    if (!selectedDwtDevice) return null;
    const canonical = normalizeScannerName(selectedDwtDevice.displayName);
    return {
      device_type: 'scanner',
      name: canonical,
      scanner_identifier: canonical,
      serial_number: null,
      ip_address: null,
      software_version: null
    };
  }, [selectedDwtDevice]);

  // Keep latest upload-handler in a ref so the registered SDK callback always
  // calls the current closure (avoids stale batchId / mutation captures).
  const handleNewPage = useCallback(() => {
    const dwt = dwtRef.current;
    const Dynamsoft = dynamsoftRef.current;
    if (!dwt || !Dynamsoft) return;

    const index = dwt.HowManyImagesInBuffer - 1;
    if (index < 0) return;

    // When the intake funnel is wired up the parent owns capacity + decode +
    // classification. We just hand off raw blobs. A re-scan replacement is a
    // strictly one-shot job: take the first sheet, drop the rest.
    const intake = onAcquireBlobRef.current;
    const replaceItemId = rescanTargetRef.current;

    // Cap on batch capacity. If the scanner kept feeding past the limit (e.g.
    // user loaded too many sheets), drop the surplus and stop the job. For a
    // single-item re-scan the cap is 1 (handled below via maxSequenceRef).
    if (sequenceRef.current > maxSequenceRef.current) {
      try {
        dwt.RemoveImage(index);
      } catch {
        // ignore
      }
      try {
        dwt.CancelAllPendingTransfers();
      } catch {
        // ignore
      }
      return;
    }

    setCounters((c) => ({ ...c, scanned: c.scanned + 1 }));

    dwt.ConvertToBlob(
      [index],
      Dynamsoft.DWT.EnumDWT_ImageType.IT_JPG,
      (blob) => {
        const seq = sequenceRef.current;
        sequenceRef.current = seq + 1;

        // Remove the page from the scanner buffer immediately so the next
        // sheet can start transferring while this one is still uploading.
        // Previously we waited for the upload to finish (sequential), which
        // was the dominant cause of the "preparing page" overlay lingering.
        try {
          dwt.RemoveImage(index);
        } catch {
          // ignore
        }

        // Dev-only: bake a synthetic QR into the captured page so QA can
        // exercise the success / mismatched / unverified branches without
        // printing physical sticking labels. No-op when injection is `off`
        // or when running a production build.
        const { mode: injectMode, sequenceOverride } =
          useDevQrInjectStore.getState();
        const injectPayload = buildInjectedPayload(isDevTools, injectMode, {
          containerId,
          sequence: sequenceOverride ?? seq
        });

        if (intake) {
          // Intake path — forward the (possibly dev-injected) blob to the
          // parent funnel. It decodes the QR, classifies it, adds clean
          // `success` pages instantly and quarantines the rest.
          injectQrIntoBlob(blob, injectPayload, { labelMm })
            .then((finalBlob) =>
              intake(finalBlob, replaceItemId ? { replaceItemId } : undefined)
            )
            .then(() => {
              setCounters((c) => ({ ...c, uploaded: c.uploaded + 1 }));
            })
            .catch(() => {
              setCounters((c) => ({ ...c, failed: c.failed + 1 }));
            });
          return;
        }

        // Legacy direct-upload path (kept for callers that don't wire the
        // intake funnel). Decode the printed sticking-stage QR before
        // uploading so the backend can bind the image to the matching slot.
        injectQrIntoBlob(blob, injectPayload, { labelMm })
          .then((finalBlob) =>
            decodeQrFromBlob(finalBlob)
              .catch(() => null)
              .then((qrCode) => ({ finalBlob, qrCode }))
          )
          .then(({ finalBlob, qrCode }) =>
            scanMutation.mutateAsync({
              batchId,
              image: finalBlob,
              expectedSequence: seq,
              qrCode
            })
          )
          .then(() => {
            setCounters((c) => ({ ...c, uploaded: c.uploaded + 1 }));
          })
          .catch(() => {
            setCounters((c) => ({ ...c, failed: c.failed + 1 }));
          });
      },
      (_code, msg) => {
        setCounters((c) => ({ ...c, failed: c.failed + 1 }));
        toast.error(msg);
      }
    );
  }, [batchId, containerId, scanMutation, isDevTools, labelMm]);

  useEffect(() => {
    onNewPageRef.current = handleNewPage;
  }, [handleNewPage]);

  // Boot the SDK + create one instance on mount
  useEffect(() => {
    let cancelled = false;
    // First terminal UI state wins — whether that's `ready`, a thrown error, or
    // the watchdog timeout below. Prevents a late SDK callback from flipping the
    // panel back out of the timeout/error state after we've given up.
    let settled = false;
    // Mint a fresh id each mount so StrictMode's double-mount never collides
    // with an in-flight CreateDWTObjectEx from the previous mount.
    const instanceId = makeInstanceId();
    const bootTimer = setTimeout(() => {
      if (cancelled || settled) return;
      settled = true;
      setErrorMessage(BOOT_TIMEOUT_SENTINEL);
      setPhase('error');
    }, BOOT_TIMEOUT_MS);
    (async () => {
      try {
        const Dynamsoft = await loadDwtSdk();
        if (cancelled) return;
        dynamsoftRef.current = Dynamsoft;

        const dwt = await new Promise<DwtInstance>((resolve, reject) => {
          Dynamsoft.DWT.CreateDWTObjectEx(
            { WebTwainId: instanceId },
            resolve,
            (e) => reject(new Error(`${e.code}: ${e.message}`))
          );
        });
        if (cancelled) {
          try {
            Dynamsoft.DWT.DeleteDWTObject(instanceId);
          } catch {
            // already gone
          }
          return;
        }
        dwtRef.current = dwt;

        dwt.RegisterEvent('OnPostTransferAsync', () => {
          onNewPageRef.current();
        });

        const rawList = await dwt.GetDevicesAsync(undefined, true);
        if (cancelled) return;
        // DWT returns the same physical scanner once per TWAIN/WIA bridge.
        // Collapse those duplicates so the picker shows one entry per device.
        const list = dedupeScannerDevices(rawList);
        setDevices(list);

        // Honor persisted device unless out of range; otherwise auto-pick Kodak.
        setSettings((s) => {
          const persistedIdx = Number(s.deviceIdx);
          if (
            s.deviceIdx !== '' &&
            Number.isFinite(persistedIdx) &&
            persistedIdx >= 0 &&
            persistedIdx < list.length
          ) {
            return s;
          }
          const preferredIdx = list.findIndex((d) =>
            /i5\d{3}|kodak/i.test(d.displayName)
          );
          const initialIdx =
            preferredIdx >= 0 ? preferredIdx : list.length ? 0 : -1;
          return {
            ...s,
            deviceIdx: initialIdx >= 0 ? String(initialIdx) : ''
          };
        });

        if (cancelled || settled) return;
        settled = true;
        setPhase('ready');
      } catch (err) {
        if (cancelled || settled) return;
        settled = true;
        setErrorMessage(formatError(err));
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(bootTimer);
      const Dynamsoft = dynamsoftRef.current;
      if (Dynamsoft) {
        try {
          Dynamsoft.DWT.DeleteDWTObject(instanceId);
        } catch {
          // ignore — already disposed or never created
        }
      }
      dwtRef.current = null;
      dynamsoftRef.current = null;
    };
  }, []);

  const startScan = useCallback(async () => {
    const dwt = dwtRef.current;
    const Dynamsoft = dynamsoftRef.current;
    if (!dwt || !Dynamsoft) return;

    const device = devices[Number(settings.deviceIdx)];
    if (!device) {
      toast.error(t('noScannerFound'));
      return;
    }

    // A per-item quarantine re-scan is one-shot: only the first sheet is taken,
    // and it replaces the held item's bytes regardless of batch capacity.
    const isRescan =
      onAcquireBlobRef.current != null && rescanTargetRef.current;
    const remaining = isRescan ? 1 : Math.max(0, total - (nextSequence - 1));
    if (remaining <= 0) {
      toast.error(t('batchFull'));
      return;
    }

    try {
      setPhase('scanning');
      setCounters({ scanned: 0, uploaded: 0, failed: 0 });
      sequenceRef.current = nextSequence;
      maxSequenceRef.current = nextSequence + remaining - 1;

      const ok = await dwt.SelectDeviceAsync(device);
      if (!ok) throw new Error('SelectDevice failed');

      await dwt.AcquireImageAsync({
        IfFeederEnabled: true, // production scanners are ADF-only
        // Duplex is locked to the dynamic form's is_double_sided flag —
        // the operator-facing toggle was removed because the sided-ness
        // of a container is a property of its registration, not a per-
        // batch decision.
        IfDuplexEnabled: isDoubleSided,
        PixelType: PIXEL_TYPE[settings.colorMode],
        Resolution: settings.resolution,
        IfShowUI: false,
        IfDisableSourceAfterAcquire: true,
        IfCloseSourceAfterAcquire: true,
        IfAutoDiscardBlankpages: settings.skipBlanks,
        PageSize: Dynamsoft.DWT.EnumDWT_CapSupportedSizes.TWSS_NONE,
        // Tell the SDK to stop accepting transfers after `remaining` pages —
        // belt-and-braces with the runtime cap in handleNewPage.
        XferCount: remaining
      });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPhase('ready');
      // Clear any one-shot re-scan target so the next normal scan job behaves
      // as a fresh batch acquisition rather than another replacement.
      onAcquireDone?.();
    }
  }, [devices, settings, isDoubleSided, nextSequence, total, t, onAcquireDone]);

  const isScanning = phase === 'scanning';
  const canStartScan =
    phase === 'ready' &&
    devices.length > 0 &&
    settings.deviceIdx !== '' &&
    !!linkedDevice;

  return (
    <div className='flex flex-col gap-3 p-4'>
      <div className='flex items-center gap-2'>
        <Printer className='h-3.5 w-3.5 text-violet-600' />
        <span className='text-[10px] font-bold tracking-wider text-slate-400 uppercase'>
          {t('scannerSection')}
        </span>
        <span
          className={`ms-auto h-2 w-2 rounded-full ${
            phase === 'ready'
              ? 'bg-emerald-500'
              : phase === 'scanning'
                ? 'animate-pulse bg-violet-500'
                : phase === 'error'
                  ? 'bg-rose-500'
                  : 'bg-slate-300'
          }`}
        />
      </div>

      {phase === 'loading' && (
        <div className='flex items-center gap-2 text-xs text-slate-500'>
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
          {t('connectingToScanner')}
        </div>
      )}

      {phase === 'error' &&
        (() => {
          const isTimeout = errorMessage === BOOT_TIMEOUT_SENTINEL;
          const kind = classifyDwtError(errorMessage ?? '');
          const title = isTimeout
            ? t('scannerConnectTimedOut')
            : kind === 'license'
              ? t('scannerLicenseExpired')
              : kind === 'service'
                ? t('dwtServiceNotInstalled')
                : t('scannerUnavailable');
          // When the container has no expected page count, the upload fallback
          // is blocked too — point at the real cause instead of the misleading
          // "upload above" hint.
          const hint =
            total === 0 ? t('noPageCountHint') : t('scannerUseUploadHint');
          return (
            <div className='rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200'>
              <div className='flex items-start gap-1.5'>
                <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                <div className='min-w-0 space-y-1'>
                  <p className='font-semibold'>{title}</p>
                  <p className='text-[11px] opacity-90'>{hint}</p>
                  {/* Verbose SDK message tucked behind a disclosure so the
                      notice stays a compact couple of lines by default. */}
                  {errorMessage && !isTimeout ? (
                    <details>
                      <summary className='cursor-pointer list-none text-[10px] font-medium underline-offset-2 opacity-70 hover:underline'>
                        {t('showDetails')}
                      </summary>
                      <p className='mt-1 font-mono text-[10px] break-words opacity-70'>
                        {errorMessage}
                      </p>
                    </details>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })()}

      {(phase === 'ready' || phase === 'scanning') && (
        <>
          {devices.length === 0 ? (
            <p className='text-xs text-rose-600'>{t('noScannerFound')}</p>
          ) : (
            <Select
              value={settings.deviceIdx}
              onValueChange={(v) => patch('deviceIdx', v)}
              disabled={isScanning}
            >
              <SelectTrigger className='w-full' size='sm'>
                <SelectValue placeholder={t('selectScanner')} />
              </SelectTrigger>
              <SelectContent>
                {devices.map((d, i) => (
                  <SelectItem key={`${d.name}-${i}`} value={String(i)}>
                    {d.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {selectedDwtDevice && linkedDevice && (
            <PermissionGate permissions={['devices.create', 'devices.edit']}>
              <button
                type='button'
                onClick={() => setDeviceFormOpen(true)}
                disabled={isScanning}
                className='flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/30 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/40'
              >
                <span className='flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300'>
                  <Check className='h-3 w-3' />
                  <span className='truncate'>
                    {t('deviceLinked', { name: linkedDevice.name })}
                  </span>
                </span>
                <span className='text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-300'>
                  {t('manageDevice')}
                </span>
              </button>
            </PermissionGate>
          )}

          {selectedDwtDevice && !linkedDevice && (
            <div className='space-y-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] dark:border-amber-900/30 dark:bg-amber-950/20'>
              <div className='flex items-start gap-1.5 text-amber-800 dark:text-amber-300'>
                <AlertTriangle className='mt-0.5 h-3 w-3 shrink-0' />
                <span>
                  {isCheckingDeviceRegistry
                    ? t('checkingDeviceRegistration')
                    : t('mustRegisterToScan')}
                </span>
              </div>
              <PermissionGate permissions={['devices.create', 'devices.edit']}>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => setDeviceFormOpen(true)}
                  disabled={isScanning || isCheckingDeviceRegistry}
                  className='h-8 w-full gap-1.5 text-xs'
                >
                  <Plus className='h-3.5 w-3.5' />
                  {t('registerInDevices')}
                </Button>
              </PermissionGate>
            </div>
          )}

          <div className='space-y-2 rounded-md border bg-slate-50 p-2.5 text-xs dark:bg-slate-800/40'>
            {/*
              Duplex toggle removed — it is now derived from the
              registration's `dynamic_form.is_double_sided` flag and passed
              in via the `isDoubleSided` prop. The form is the single source
              of truth for whether a container's pages are double-sided, so
              the per-batch override would only let operators contradict it.

              <SettingRow label={t('duplex')}>
                <Switch
                  checked={settings.duplex}
                  onCheckedChange={(v) => patch('duplex', v)}
                  disabled={isScanning}
                />
              </SettingRow>
            */}

            {/* Locked duplex indicator — same SettingRow visual rhythm as
                the surrounding settings so it reads as "this is set for
                you, not by you". Coloured by mode so the operator can
                tell at a glance which run they're about to start. */}
            <SettingRow label={t('duplex')}>
              {isDoubleSided ? (
                <span className='inline-flex items-center gap-1 rounded-md bg-violet-100 px-2 py-0.5 text-[11px] font-bold text-violet-700 dark:bg-violet-950/40 dark:text-violet-300'>
                  {t('duplexLockedDouble')}
                </span>
              ) : (
                <span className='inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300'>
                  {t('duplexLockedSingle')}
                </span>
              )}
            </SettingRow>

            <SettingRow label={t('colorMode')}>
              <Select
                value={settings.colorMode}
                onValueChange={(v) => patch('colorMode', v as ColorMode)}
                disabled={isScanning}
              >
                <SelectTrigger size='sm' className='h-7 w-32'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='color'>{t('colorModeColor')}</SelectItem>
                  <SelectItem value='gray'>{t('colorModeGray')}</SelectItem>
                  <SelectItem value='bw'>{t('colorModeBw')}</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label={t('resolution')}>
              <Select
                value={String(settings.resolution)}
                onValueChange={(v) =>
                  patch('resolution', Number(v) as Resolution)
                }
                disabled={isScanning}
              >
                <SelectTrigger size='sm' className='h-7 w-32'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='200'>{t('dpi200')}</SelectItem>
                  <SelectItem value='300'>{t('dpi300')}</SelectItem>
                  <SelectItem value='600'>{t('dpi600')}</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label={t('skipBlanks')}>
              <Switch
                checked={settings.skipBlanks}
                onCheckedChange={(v) => patch('skipBlanks', v)}
                disabled={isScanning}
              />
            </SettingRow>
          </div>

          <div className='grid grid-cols-3 gap-1.5 text-center text-[10px]'>
            <CounterTile
              value={counters.scanned}
              label={t('pagesScanned')}
              tone='neutral'
            />
            <CounterTile
              value={counters.uploaded}
              label={t('pagesUploaded')}
              tone='success'
            />
            <CounterTile
              value={counters.failed}
              label={t('pagesFailed')}
              tone='danger'
            />
          </div>

          <Button
            size='sm'
            className='h-9 w-full gap-2 bg-violet-600 hover:bg-violet-700'
            onClick={() => void startScan()}
            disabled={!canStartScan}
          >
            {isScanning ? (
              <>
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
                {t('scanInProgress')}
              </>
            ) : (
              <>
                <ScanLine className='h-3.5 w-3.5' />
                {t('startScan')}
              </>
            )}
          </Button>
        </>
      )}

      <DeviceFormDialog
        open={deviceFormOpen}
        onOpenChange={setDeviceFormOpen}
        device={linkedDevice}
        prefill={linkedDevice ? null : devicePrefill}
      />
    </div>
  );
}

function SettingRow({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className='flex items-center justify-between gap-2'>
      <span className='text-slate-600 dark:text-slate-300'>{label}</span>
      {children}
    </div>
  );
}

function CounterTile({
  value,
  label,
  tone
}: {
  value: number;
  label: string;
  tone: 'neutral' | 'success' | 'danger';
}) {
  const palette = {
    neutral:
      'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200',
    success:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/30 dark:bg-emerald-950/20 dark:text-emerald-300',
    danger:
      'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/30 dark:bg-rose-950/20 dark:text-rose-300'
  }[tone];

  return (
    <div className={`rounded-md border p-1.5 ${palette}`}>
      <p className='text-base leading-none font-bold'>{value}</p>
      <p className='mt-1 uppercase opacity-80'>{label}</p>
    </div>
  );
}
